import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { SlidersHorizontal, X, Check } from "lucide-react";
import { PLANTS, FILTERS, CATEGORIES } from "../../data/plants";
import { SITE_IMAGES } from "../../data/siteImages";
import ProductCard from "../../components/fancyplants/ProductCard";
import PlantImage from "../../components/fancyplants/PlantImage";
import Seo from "../../components/fancyplants/Seo";
import { EmptyState } from "../../components/fancyplants/states";

const SORTS = [
  { value: "featured", label: "Featured" },
  { value: "price-asc", label: "Price: low to high" },
  { value: "price-desc", label: "Price: high to low" },
  { value: "name", label: "A–Z" },
];

export default function ShopPage() {
  const [params, setParams] = useSearchParams();
  const [sheetOpen, setSheetOpen] = useState(false);

  const active = {
    category: params.get("category") || "",
    light: params.getAll("light"),
    placement: params.getAll("placement"),
    size: params.getAll("size"),
    season: params.getAll("season"),
    petSafe: params.get("petSafe") === "1",
    maxPrice: params.get("maxPrice") ? Number(params.get("maxPrice")) : null,
    sort: params.get("sort") || "featured",
  };

  const toggleMulti = (key, value) => {
    const next = new URLSearchParams(params);
    const current = next.getAll(key);
    next.delete(key);
    const updated = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    updated.forEach((v) => next.append(key, v));
    setParams(next, { replace: true });
  };

  const setSingle = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const clearAll = () => setParams(new URLSearchParams(), { replace: true });

  const results = useMemo(() => {
    let list = PLANTS.filter((p) => {
      if (active.category && p.category !== active.category) return false;
      if (active.light.length && !active.light.includes(p.traits.light)) return false;
      if (active.placement.length && !active.placement.includes(p.traits.placement)) return false;
      if (active.size.length && !active.size.includes(p.traits.size)) return false;
      if (active.season.length && !active.season.includes(p.traits.season)) return false;
      if (active.petSafe && !p.traits.petSafe) return false;
      if (active.maxPrice != null && p.price > active.maxPrice) return false;
      return true;
    });
    switch (active.sort) {
      case "price-asc": list = [...list].sort((a, b) => a.price - b.price); break;
      case "price-desc": list = [...list].sort((a, b) => b.price - a.price); break;
      case "name": list = [...list].sort((a, b) => a.name.localeCompare(b.name)); break;
      default: list = [...list].sort((a, b) => (b.stock > 0) - (a.stock > 0));
    }
    return list;
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeCount =
    (active.category ? 1 : 0) +
    active.light.length +
    active.placement.length +
    active.size.length +
    active.season.length +
    (active.petSafe ? 1 : 0) +
    (active.maxPrice != null ? 1 : 0);

  const Filters = (
    <div className="space-y-7">
      <FilterGroup title="Category">
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setSingle("category", active.category === c ? "" : c)}
              className={`chip ${active.category === c ? "chip-active" : ""}`}
            >
              {c}
            </button>
          ))}
        </div>
      </FilterGroup>

      <FilterGroup title="Pet household">
        <button
          onClick={() => setSingle("petSafe", active.petSafe ? "" : "1")}
          className={`chip ${active.petSafe ? "chip-active" : ""}`}
        >
          {active.petSafe && <Check size={12} />} Pet-safe only
        </button>
      </FilterGroup>

      {Object.entries(FILTERS).map(([key, opts]) => (
        <FilterGroup key={key} title={labelFor(key)}>
          <div className="flex flex-wrap gap-2">
            {opts.map((o) => {
              const on = active[key].includes(o.value);
              return (
                <button
                  key={o.value}
                  onClick={() => toggleMulti(key, o.value)}
                  className={`chip ${on ? "chip-active" : ""}`}
                >
                  {on && <Check size={12} />} {o.label}
                </button>
              );
            })}
          </div>
        </FilterGroup>
      ))}

      <FilterGroup title="Budget">
        <div className="flex flex-wrap gap-2">
          {[20, 40, 70].map((cap) => (
            <button
              key={cap}
              onClick={() => setSingle("maxPrice", active.maxPrice === cap ? "" : String(cap))}
              className={`chip ${active.maxPrice === cap ? "chip-active" : ""}`}
            >
              Under €{cap}
            </button>
          ))}
        </div>
      </FilterGroup>

      {activeCount > 0 && (
        <button onClick={clearAll} className="text-sm font-medium text-terracotta hover:underline">
          Clear all filters
        </button>
      )}
    </div>
  );

  return (
    <div className="pt-24">
      <Seo
        title="Shop"
        description="Browse the full Fancy Plants collection — unusual foliage, trailing plants and hardy exotics chosen for the Irish climate. Filter by light, placement, pet-safety and budget."
        path="/shop"
      />
      {/* Page head */}
      <header className="border-b border-line bg-canvas-raised">
        <div className="container-fp grid items-center gap-8 py-10 lg:grid-cols-[1fr_360px]">
          <div>
          <p className="eyebrow mb-2">The shop</p>
          <h1 className="t-h1">Find your next plant</h1>
          <p className="t-lead mt-2 max-w-2xl">
            Browse the full collection. Filter by light, placement, pet-safety and budget —
            everything is chosen to do well in an Irish home or garden.
          </p>
          </div>
          <PlantImage
            src={SITE_IMAGES.shopHero}
            alt="A healthy monstera plant from the Fancy Plants collection"
            ratio="4/3"
            className="rounded-3xl"
            priority
          />
        </div>
      </header>

      <div className="container-fp grid gap-10 py-10 lg:grid-cols-[260px_1fr]">
        {/* Desktop sidebar */}
        <aside className="hidden lg:block">
          <div className="sticky top-24">{Filters}</div>
        </aside>

        <div>
          {/* Controls bar */}
          <div className="mb-6 flex items-center justify-between gap-3">
            <button
              onClick={() => setSheetOpen(true)}
              className="btn-outline lg:hidden"
            >
              <SlidersHorizontal size={16} /> Filters
              {activeCount > 0 && (
                <span className="ml-1 rounded-full bg-forest-700 px-2 text-xs text-canvas-raised">
                  {activeCount}
                </span>
              )}
            </button>
            <p className="hidden text-sm text-ink-soft lg:block">
              {results.length} plant{results.length === 1 ? "" : "s"}
            </p>
            <label className="flex items-center gap-2 text-sm">
              <span className="hidden text-ink-soft sm:inline">Sort</span>
              <select
                value={active.sort}
                onChange={(e) => setSingle("sort", e.target.value)}
                className="rounded-full border border-line bg-canvas-raised px-3 py-2 text-sm focus:border-forest-500"
              >
                {SORTS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </label>
          </div>

          {/* Results: empty / grid */}
          {results.length === 0 ? (
            <EmptyState
              title="No plants match those filters"
              message="Nothing fits that exact combination right now. Loosen a filter or two and you'll likely find something close."
              action={
                <button onClick={clearAll} className="btn-forest">
                  Clear filters
                </button>
              }
            />
          ) : (
            <div className="grid grid-cols-2 gap-5 md:grid-cols-3">
              {results.map((p, i) => (
                <ProductCard key={p.id} plant={p} index={i} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Mobile bottom sheet */}
      <AnimatePresence>
        {sheetOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setSheetOpen(false)}
              className="fixed inset-0 z-[60] bg-green-900/40 lg:hidden"
            />
            <motion.div
              initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
              transition={{ type: "spring", stiffness: 320, damping: 34 }}
              className="fixed inset-x-0 bottom-0 z-[70] max-h-[85vh] overflow-y-auto rounded-t-3xl bg-canvas p-6 lg:hidden"
            >
              <div className="mb-4 flex items-center justify-between">
                <h2 className="font-display text-xl">Filters</h2>
                <button onClick={() => setSheetOpen(false)} aria-label="Close filters">
                  <X size={22} />
                </button>
              </div>
              {Filters}
              <button onClick={() => setSheetOpen(false)} className="btn-brass mt-6 w-full">
                Show {results.length} plant{results.length === 1 ? "" : "s"}
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

function FilterGroup({ title, children }) {
  return (
    <div>
      <h3 className="mb-3 text-sm font-semibold text-ink">{title}</h3>
      {children}
    </div>
  );
}

function labelFor(key) {
  return {
    light: "Light",
    placement: "Placement",
    size: "Size",
    season: "Best season",
  }[key];
}
